import {
	ArcElement,
	CategoryScale,
	Chart as ChartJS,
	Legend,
	LineElement,
	LinearScale,
	PointElement,
	Title,
	Tooltip,
} from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { Suspense, lazy, startTransition, useEffect, useState } from 'react'
import { Doughnut, Line } from 'react-chartjs-2'

import { useScheduleList } from '@/modules/schedule/hooks/use-schedule-list.hook'
import {
	Card,
	CardContent,
	CardHeader,
} from '@/modules/shared/components/ui/card'
import { cn } from '@/modules/shared/lib/utils'
import { priceFormatter } from '@/modules/shared/utils/formatter'

import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react'
import { DemonstrativoMetaFamiliaTableComponent } from '../components/demonstrativo-meta-familia-table.component'
import { DemonstrativoMetaFamiliaTotalMesTableComponent } from '../components/demonstrativo-meta-familia-total-mes-table.component'
import { HomeTableCardComponent } from '../components/home-table-card-component'
import { homeCardClientReportFields } from '../constants/home-card-client-report'
import { homeCardGoalReportFields } from '../constants/home-card-goal-report'
import { homeMonth } from '../constants/home-month'
import { useCalendarWeekMonth } from '../hooks/use-calendar-week-month.hook'
import type { useHome } from '../hooks/use-home.hook'
import ScheduleListInterface from '@/modules/schedule/interfaces/schedule-list.interface'
import { BottomMenuComponent } from '@/modules/shared/components/bottom-menu.component'
import { Button } from '@/modules/shared/components/ui/button'
import { useAuthStore } from '../../shared/stores/auth.store'
import { useDemonstrativoMetaFamila } from '../hooks/use-demonstrativo-meta-familia'
import { useDemonstrativoMetaFamilaTotalMes } from '../hooks/use-demonstrativo-meta-familia-total-mes'

ChartJS.register(
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend,
	ArcElement,
	ChartDataLabels,
)

type HomeInterfaceProps = ReturnType<typeof useHome>

export function HomeInterface({
	currentMonth,
	dataChartGoalReport,
	dataClientReport,
	showPercentGoals,
	dataGoalReport,
	isLoadingCustomersReports,
	goalsReport,
	positivacaoDeClientesData,
	handleChangeTypeDashboard,
	typeDashboard,
	showPositivatedClients,
	doughnutData,
}: HomeInterfaceProps) {
	const nivelAcesso = useAuthStore((state) => state.nivelAcesso)
	const showSchedule = nivelAcesso !== 3
	const scheduleListHook = useScheduleList({ enabled: showSchedule })
	const {
		handleNavigateCalendarWeek,
		isLoadingCalendarWeekMonth,
		selectedCalendarWeek,
		selectedSemanaIdParams,
	} = useCalendarWeekMonth()

	const {
		dataDemonstrativoMetaFamilaTotalMes,
		isLoading,
		mes,
		ano,
		mesAnterior,
		proximoMes,
	} = useDemonstrativoMetaFamilaTotalMes()

	const { dataDemonstrativoMetaFamila, isLoadingDemonstrativoMetaFamila } =
		useDemonstrativoMetaFamila(selectedSemanaIdParams)

	const { metaPorQuantidade } = useAuthStore()

	return (
		<div className="space-y-1 md:space-y-2 ">

			<div className="w-full space-y-1 md:space-y-2">
				<div className="w-full grid grid-cols-2 md:grid-cols-6 gap-1 md:gap-2">
					{homeCardGoalReportFields?.map((item) => (
						<Card key={item.id}>
							<CardContent className="p-4">
								<p className="uppercase text-xs">{item.title}</p>

								<p className="pt-1 md:pt-2 font-bold text-sm">
									{item.valueFormatted && !metaPorQuantidade
										? priceFormatter.format(
											dataGoalReport?.relVendasMetas?.resumo[item.field] ?? 0,
										)
										: dataGoalReport?.relVendasMetas?.resumo[item.field] ??
										dataGoalReport?.relPerfilClientes[item.field] ??
										0}
								</p>
							</CardContent>
						</Card>
					))}
				</div>
				<Card className="w-full bg-white">
					<CardHeader>
						<div className="flex justify-between flex-row">
							<p className="text-xs sm:text-sm uppercase font-bold ">Metas</p>

							<p className="text-xs sm:text-sm uppercase">{`${homeMonth[currentMonth.getMonth()]} - ${currentMonth.getFullYear()}`}</p>
						</div>
					</CardHeader>
					<CardContent>
						<div className="h-[232px] sm:h-[264px] md:h-[324px] lg:h-[360px]">
							<Line
								data={dataChartGoalReport.data}
								options={dataChartGoalReport.options}
							/>
						</div>
					</CardContent>
				</Card>
			</div>

			{/*dataDemonstrativoMetaFamilaTotalMes.length > 0 && (*/
				<div className="w-full space-y-1 md:space-y-2">
					<Card className="w-full bg-white">
						<CardHeader>
							<div className="flex justify-between flex-row">
								<p className="text-xs sm:text-sm uppercase font-bold ">
									Meta por familia/mês
								</p>
							</div>
						</CardHeader>
						<CardContent>
							<div className="mb-3 flex h-11 items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-2">
								<Button
									aria-label="Mes anterior"
									className="h-8 w-8"
									disabled={isLoading}
									onClick={mesAnterior}
									size="icon"
									type="button"
									variant="ghost"
								>
									<ChevronLeft className="h-4 w-4" />
								</Button>

								<p className="px-2 text-center text-xs font-semibold uppercase text-gray-700 sm:text-sm">
									{`${homeMonth[mes - 1]} - ${ano}`}
								</p>

								<Button
									aria-label="Proximo mes"
									className="h-8 w-8"
									disabled={isLoading}
									onClick={proximoMes}
									size="icon"
									type="button"
									variant="ghost"
								>
									<ChevronRight className="h-4 w-4" />
								</Button>
							</div>
							<div
								className={cn(
									'overflow-auto md:h-[324px] lg:h-[360px]',
									isLoading && 'h-[200px]',
									!isLoading &&
									dataDemonstrativoMetaFamilaTotalMes.length <= 0 &&
									'h-20 md:h-20 lg:h-20',
									!isLoading &&
									dataDemonstrativoMetaFamilaTotalMes.length > 0 &&
									'h-[560px] sm:h-[600px] md:h-[400px] lg:h-[500px]',
								)}
							>
								<DemonstrativoMetaFamiliaTotalMesTableComponent
									data={dataDemonstrativoMetaFamilaTotalMes}
									isLoading={isLoading}
									metaPorQuantidade={metaPorQuantidade}
								/>
							</div>
						</CardContent>
					</Card>
				</div>
      /*)*/}

			{/*dataDemonstrativoMetaFamila.length > 0 && (*/
				<div className="w-full space-y-1 md:space-y-2">
					<Card className="w-full bg-white">
						<CardHeader>
							<div className="flex justify-between flex-row">
								<p className="text-xs sm:text-sm uppercase font-bold ">
									Meta por família Semanal
								</p>
							</div>
						</CardHeader>
						<CardContent>
							<div className="mb-3 flex h-11 items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-2">
								<Button
									aria-label="Semana anterior"
									className="h-8 w-8"
									disabled={!selectedCalendarWeek || isLoadingCalendarWeekMonth}
									onClick={() => handleNavigateCalendarWeek('previous')}
									size="icon"
									type="button"
									variant="ghost"
								>
									<ChevronLeft className="h-4 w-4" />
								</Button>

								<p className="px-2 text-center text-xs font-semibold uppercase text-gray-700 sm:text-sm">
									{selectedCalendarWeek?.descricaoSemana ?? 'Carregando semana'}
								</p>

								<Button
									aria-label="Próxima semana"
									className="h-8 w-8"
									disabled={!selectedCalendarWeek || isLoadingCalendarWeekMonth}
									onClick={() => handleNavigateCalendarWeek('next')}
									size="icon"
									type="button"
									variant="ghost"
								>
									<ChevronRight className="h-4 w-4" />
								</Button>
							</div>
							<div
								className={cn(
									'overflow-auto md:h-[324px] lg:h-[360px]',
									isLoadingDemonstrativoMetaFamila && 'h-[200px]',
									!isLoadingDemonstrativoMetaFamila &&
									dataDemonstrativoMetaFamila.length <= 0 &&
									'h-20 md:h-20 lg:h-20',
									!isLoadingDemonstrativoMetaFamila &&
									dataDemonstrativoMetaFamila.length > 0 &&
									'h-[560px] sm:h-[600px] md:h-[400px] lg:h-[500px]',
								)}
							>
								<DemonstrativoMetaFamiliaTableComponent
									data={dataDemonstrativoMetaFamila}
									isLoading={isLoadingDemonstrativoMetaFamila}
									metaPorQuantidade={metaPorQuantidade}
								/>
							</div>
						</CardContent>
					</Card>
				</div>
      /*)*/}

			<div className="grid md:grid-cols-2 gap-1 md:gap-2">
				<Card className="w-full bg-white">
					<CardHeader>
						<div className="flex justify-between flex-row">
							<p className="text-xs sm:text-sm uppercase font-bold ">
								Metas (%)
							</p>

							<p className="text-xs sm:text-sm uppercase">{`${homeMonth[currentMonth.getMonth()]} - ${currentMonth.getFullYear()}`}</p>
						</div>
					</CardHeader>
					<CardContent>
						<div className="h-[200px] sm:h-[264px] md:h-[324px] lg:h-[360px] justify-center flex items-center">
							{showPercentGoals ? (
								<Doughnut
									data={doughnutData}
									options={{
										circumference: 180,
										rotation: 270,
										layout: {
											padding: 3,
										},
										plugins: {
											legend: {
												display: true,
												position: 'bottom' as const,
												labels: {
													font: {
														size: 12,
													},
												},
											},
											datalabels: {
												formatter: (value: number) => `${value.toFixed(1)}%`,
											},
										},
									}}
								/>
							) : (
								<p className="text-center w-full text-gray-500">
									Não há metas para este mês
								</p>
							)}
						</div>
					</CardContent>
				</Card>

				<Card className="w-full bg-white">
					<CardHeader>
						<div className="flex justify-between flex-row">
							<div className="text-xs sm:text-sm uppercase font-bold ">
								Positivação de clientes{' '}
								<div className=" top-32">
									<span className="font-normal normal-case">
										Total: {goalsReport?.relResumoPositivacao?.total}
									</span>
								</div>
							</div>
							<p className="text-xs sm:text-sm uppercase">{`${homeMonth[currentMonth.getMonth()]} - ${currentMonth.getFullYear()}`}</p>
						</div>
					</CardHeader>
					<CardContent>
						<div className="h-[300px] sm:h-[264px] md:h-[324px] lg:h-[360px] justify-center flex items-center relative">
							<>
								{showPositivatedClients ? (
									<Doughnut
										data={positivacaoDeClientesData}
										options={{
											plugins: {
												legend: {
													position: 'bottom' as const,
												},
												datalabels: {
													display: true,
													color: 'white',
												},
											},
										}}
									/>
								) : (
									<p className="text-center w-full text-gray-500">
										Não há positivação de clientes para este mês
									</p>
								)}
							</>
						</div>
					</CardContent>
				</Card>
			</div>

			<div className={cn(
				'w-full mt-1 md:mt-2 grid grid-cols-2 gap-1 md:gap-2',
				showSchedule ? 'md:grid-cols-5' : 'md:grid-cols-4',
			)}>
				{homeCardClientReportFields.filter((item) => showSchedule || item.id !== 5).map((item) => (
					<Card
						key={item.id}
						className={cn(
							'w-full transition-all duration-300 ease-in-out border-2',
							item.isButton ? 'cursor-pointer' : 'cursor-default',
							typeDashboard === item.id
								? 'bg-sky-200/70'
								: ' border-gray-200 bg-white',
						)}
						onClick={() => handleChangeTypeDashboard(item.id)}
					>
						<CardContent className="p-4">
							<p className="uppercase text-xs">{item.title}</p>

							{item.id === 5 ? (
								<p className="text-sm font-bold">
									{scheduleListHook.schedules.length}
								</p>
							) : (
								<p className="text-sm font-bold">
									{dataGoalReport?.relPerfilClientes[item.field] ?? 0}
								</p>
							)}
						</CardContent>
					</Card>
				))}
			</div>

			{showSchedule && typeDashboard === 5 ? (
				<Card className="w-full bg-white">
					<CardContent>
						<ScheduleListInterface isDashoboardPage {...scheduleListHook} />
					</CardContent>
				</Card>
			) : (
				<>
					{isLoadingCustomersReports ? (
						<div className="w-full flex flex-1 justify-center h-[50vh] items-center">
							<Loader2 className="w-10 h-10 text-primary text-center animate-spin" />
						</div>
					) : (
						<HomeTableCardComponent dataClientReport={dataClientReport} />
					)}
				</>
			)}

			<BottomMenuComponent />
		</div>
	)
}
